/**
 * Functions that deal with currency.
 *
 * @module component/util/currency
 */

/**
 * Convert an integer number into an internationalized Canadian currency string.
 *
 * For example, 39 or 3900 becomes $39.00 or 39,00 $
 *
 * Note: the return value uses non-breaking spaces (ie char code 160) instead of
 * normal spaces (ie char chode 32)
 *
 * @example
 * // Returns $42.00
 * document.body.dataset.lang = 'en'
 * let currency = format(42)
 *
 * // Returns $0.42
 * currency = format(42, true)
 *
 * // Returns 42.00
 * currency = format(42, false, true)
 *
 * // Returns 42,00 $
 * document.body.dataset.lang = 'fr'
 * currency = format(42)
 *
 * @param {number} value
 * An integer that is interpreted to either be dollars (if in_cents is false) or
 * cents (if in_cents is true).
 *
 * @param {boolean} [in_cents=false]
 * Determines if the value parameter is interpreted as a dollar value or a cents
 * value.
 *
 * @param {boolean} [with_symbol=true]
 * Determines if the returned value includes the currency symbol or not.
 *
 * @returns {string}
 *
 * @throws {TypeError}
 * An exception is thrown if the value parameter is not an integer >= 0
 *
 */
export function format(value, in_cents = false, with_symbol = true) {
    // Validate value arg is an integer
    validate_args(value, in_cents);
    let lang = getLang();
    lang = lang === 'zh' ? 'en' : lang;
    let country_code = `${lang}-CA`;
    if (in_cents) {
        // Convert cents to dollars
        value = value / 100;
    }
    let options;
    if (with_symbol) {
        options = {
            style: 'currency',
            currency: 'CAD',
            currencyDisplay: 'symbol',
        };
    } else {
        options = {
            style: 'decimal',
            minimumFractionDigits: 2,
        };
    }
    let formatter = new Intl.NumberFormat(country_code, options);
    return formatter.format(value);
}

/**
 * Remove all non-decimal characters from a price string
 *
 * @example
 * // Returns 42
 * let clean_currency = clean('$42.00')
 *
 * @param {string} price_str
 * A string that represents a currency value eg $42.00
 *
 * @returns {number}
 *
 * @throws {TypeError}
 * An exception will be thrown if price_str is not a string
 *
 */
export function clean(price_str) {
    let price_str_type = typeof price_str;
    if (price_str_type !== 'string') {
        let errmsg =
            'price_str arg expected string value, ' +
            `got ${price_str_type} instead`;
        throw new TypeError(errmsg);
    }

    return price_str.replace(/[^\d]/g, '') / 100;
}

function validate_args(value, in_cents) {
    let value_type = typeof value;
    let in_cents_type = typeof in_cents;
    let is_nat =
        value_type === 'number' && parseInt(`${value}`) === value && value >= 0;
    let errmsg = '';
    if (!is_nat) {
        errmsg = `value arg expected natural number, got ${value_type} instead`;
    } else if (in_cents_type !== 'boolean') {
        errmsg =
            'in_cents arg expected boolean value, ' +
            `got ${in_cents_type} instead`;
    }

    if (errmsg) {
        throw new TypeError(errmsg);
    }
}

function getLang() {
    let ret = document.body.dataset.lang;

    // Use en-CA representation of currency for zh-CA locale. This is to ensure
    // that currency is $XX.XX instead of CA$XX.XX
    if (ret === 'ch') {
        ret = 'en';
    }
    return ret;
}
